import React, { useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Animated, { 
  useSharedValue, 
  useAnimatedStyle, 
  withRepeat, 
  withTiming, 
  Easing,
} from 'react-native-reanimated';
import { useFonts, Inter_500Medium } from '@expo-google-fonts/inter';
import LoadingIcon from './icons/LoadingIcon';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';

type PublishingStatus = 'publishing' | 'error' | 'success';

interface PublishingStatusBarProps {
  status: PublishingStatus;
  visible: boolean;
}

export default function PublishingStatusBar({ status, visible }: PublishingStatusBarProps) {
  const rotation = useSharedValue(0);
  const { t } = useLanguage();

  const [fontsLoaded] = useFonts({
    Inter_500Medium,
  });

  useEffect(() => {
    if (visible && status === 'publishing') {
      rotation.value = withRepeat(
        withTiming(360, {
          duration: 1500,
          easing: Easing.linear,
        }),
        -1,
        false
      );
    } else {
      rotation.value = 0;
    }
  }, [status, visible]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  if (!fontsLoaded || !visible) {
    return null;
  }

  const getStatusContent = () => {
    switch (status) {
      case 'publishing':
        return {
          text: t('community.publishing.publishing'),
          icon: (
            <Animated.View style={animatedStyle}>
              <LoadingIcon size={24} color="#669AB7" />
            </Animated.View>
          ),
        };
      case 'error':
        return {
          text: t('community.publishing.error'),
          icon: (
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path 
                d="M18 6L6 18M6 6L18 18" 
                stroke="#D32F2F" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          ),
        };
      case 'success':
        return {
          text: t('community.publishing.success'),
          icon: (
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path 
                d="M20 6L9 17L4 12" 
                stroke="#2E7D32" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          ),
        };
    }
  };

  const content = getStatusContent();

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        {content.icon}
      </View>
      <Text style={styles.text}>{content.text}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#FFFFFF',
  },
  iconContainer: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16.8,
    color: '#757C85',
  },
});
